<script lang="ts">
  import { search } from "$lib/stores/search";
  import { fade } from "svelte/transition";
  import BasketStatus from "$lib/components/Nav/BasketStatusIcon/BasketStatusIcon.svelte";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import Link from "$lib/components/Link/Link.svelte";
  import NavMenuItem from "$lib/components/Nav/NavMenuItem/NavMenuItem.svelte";
  import MobileNav from "$lib/components/Nav/MobileNav/MobileNav.svelte";
  import { locale, _ } from "svelte-i18n";
  import type { Nav, Banner as BannerData } from "$lib/gql/genCms/codegen";
  import { PageTheme } from "$lib/gql/genCms/codegen";
  import Container from "$lib/components/Container/Container.svelte";
  import { onMount } from "svelte";
  import MobileSearch from "$lib/components/Nav/MobileSearch/MobileSearch.svelte";
  import BannerWrapper from "$lib/components/Banner/BannerWrapper/BannerWrapper.svelte";
  import RegionSelectorCTA from "$lib/components/Footer/RegionSelectorCTA/RegionSelectorCTA.svelte";
  import { fly } from "svelte/transition";
  import { circInOut } from "svelte/easing";
  import { scrollTopThreshold } from "./NavHeader.data";
  import cfg from "$lib/config/config";
  import MiniCart from "$lib/components/MiniCart/MiniCart.svelte";
  import { linkTo } from "$lib/utils/url";
  import SearchInput from "$lib/components/Nav/SearchInput/SearchInput.svelte";
  import { scrolledUpStore } from "$lib/stores/scrolledUp";
  import { assets } from "$app/paths";
  import type { RegionStore } from "$lib/stores/region";
  import { cart } from "$lib/stores/cart";

  export let nav: Nav;
  export let banners: BannerData[];
  export let logoOnly = false;
  export let country = "";
  export let language = "";
  export let searchEnabled =
    cfg.SEARCH_ENABLED && cfg.SEARCH_ENABLED === "true";
  export let theme: PageTheme = PageTheme.Dark;
  export let isLandingPage = false;
  export let regionInfo: RegionStore;
  $: dark =
    theme === PageTheme.Light && isLandingPage
      ? true
      : theme === PageTheme.Dark && isLandingPage
        ? false
        : true;
  $: darkBg = false;

  let scrollTop = 0;
  let scrolledUp = false;
  let bannerShowing = true;
  let viewportWidth: number;
  let isMobile = false;
  let lgWidth = 1280;
  let bannerHasLinks: boolean = false;
  let miniCart: MiniCart;
  if (banners && banners.length > 0) {
    banners.forEach(banner => {
      if (banner.link1 !== null || banner.link2 !== null) {
        bannerHasLinks = true;
      }
    });
  }

  $: bannerShowing = banners && banners.length > 0;
  $: isVisible = false;

  $: quantity = $cart?.totalQuantity;

  let mobileNavOpen = false;
  let mobileSearchOpen = false;
  function toggleMobileNav() {
    mobileNavOpen = !mobileNavOpen;
  }
  function closeMobileNav() {
    mobileNavOpen = false;
  }
  $: if (typeof window !== "undefined")
    window.document.body.classList.toggle("noscroll", mobileNavOpen);
  const openMobileSearch = () => {
    $search = !$search;
  };
  const closeMobileSearch = () => {
    $search = !$search;
  };
  onMount(() => {
    const bodyStyle = getComputedStyle(document.body);
    lgWidth = Number(bodyStyle.getPropertyValue("--lg").replace(/\D/g, ""));
    scrollTop = getScrollTop();
    addEventListener("scroll", updateScroll);
    isVisible = true;
    return () => {
      removeEventListener("scroll", updateScroll);
    };
  });
  let scrollUpAmount = 0;
  const updateScroll = () => {
    const newScroll = getScrollTop();
    if (newScroll > scrollTop) {
      scrollUpAmount = 0;
      scrolledUp = false;
      $scrolledUpStore = false;
      if (!isMobile) {
        $search = false;
      }
    } else if (newScroll < scrollTop) {
      scrollUpAmount++;
      if (scrollUpAmount > 20) {
        scrolledUp = true;
        if (scrollTop > 462) {
          $scrolledUpStore = true;
        }
      }
    }
    scrollTop = newScroll;
  };
  const getScrollTop = (): number => {
    return window.pageYOffset || document.documentElement.scrollTop;
  };
  $: isMobile = viewportWidth < lgWidth;
  const handleMiniCartHover = () => {
    if (!isMobile) {
      miniCart.handleOpenMiniCart();
      miniCart.isMouseOver(true);
    }
  };
  const handleMiniCartHoverLeave = () => {
    if (!isMobile) {
      miniCart.handleCloseMiniCart();
      miniCart.isMouseOver(false);
    }
  };
  let disableAutoDismiss = false;
  $: colourTheme =
    (isMobile && scrollTop > scrollTopThreshold && dark) ||
    (mobileNavOpen && dark)
      ? "on-surface"
      : dark
        ? "on-surface"
        : "surface";
  $: iconTheme =
    mobileNavOpen && dark
      ? "var(--on-surface)"
      : scrollTop > scrollTopThreshold && dark
        ? "var(--on-surface)"
        : scrollTop < scrollTopThreshold && dark
          ? "var(--on-surface)"
          : dark
            ? "var(--on-surface)"
            : "var(--surface)";

  let navItems = nav.items.map(item => {
    return { ...item, open: false };
  });

  function onMouseExitNav() {
    navItems = nav.items.map(item => {
      return { ...item, open: false };
    });
  }
</script>

<style lang="scss">
  :global(body.noscroll) {
    overflow: hidden;
  }

  .nav-header {
    opacity: 0;

    &.isVisible {
      opacity: 1;
      transition: all $animDuration;
    }
  }

  .nav {
    display: flex;
    flex-direction: column;
    position: relative;
    transition: all 300ms ease-in-out;

    &--light-bg {
      background-color: $surface;
    }

    &--dark-bg {
      background-color: $on-surface;
    }

    &--has-border {
      @include retina {
        border-bottom: 0.5px solid $on-surface;
      }
      border-bottom: 1px solid $on-surface;
    }

    @include breakpoint(lg) {
      &--light {
        &__scrolled,
        &:hover {
          background-color: $on-surface;
        }
      }
      &--dark {
        &__scrolled,
        &:hover {
          background-color: $surface;
        }
      }
    }
    &.sm-nav-open {
      height: 100vh;
    }
    &--mobile {
      &__scrolled {
        background: $on-surface;
        border-bottom: 1px solid $background;
      }
      &__dark {
        background: $inverse-on-surface;
        border: 1px solid $background;
      }

      @include retina {
        &__dark {
          border-top: 0.5px solid $background;
          border-left: 0;
          border-right: 0;
        }
      }
    }
    &-header {
      position: -webkit-sticky;
      position: sticky;
      top: 0;
      z-index: 1001;
    }
    &--wrapper {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      &__no-inter {
        pointer-events: none;
      }
    }
    &--search-bar {
      display: none;
      @include breakpoint(lg) {
        display: block;
        max-width: 220px;
      }
    }
    &-menu {
      display: none;
      ul {
        padding: 0;
        margin: 0 auto;
        color: $on-surface;
        max-width: $lg;
        li {
          display: block;
          float: left;
          &:not(:last-child) {
            margin-right: 24px;
          }
        }
      }
      ul::after {
        content: "";
        display: block;
        clear: both;
      }
    }
    &-icons {
      &-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        > *:not(:last-child) {
          margin-right: 14px;
        }
        @include breakpoint(lg) {
          flex: 1;
          > *:not(:last-child) {
            margin-right: 24px;
          }
        }
      }
      &--account {
        display: none;
        @include breakpoint(md) {
          display: block;
        }
      }
      &--search-btn {
        background: none;
        border: none;
        margin: 0;
        padding: 0;
      }
      &--region-selector {
        display: none;
        @include breakpoint(lg) {
          display: block;
        }
        &__btn {
          border: none;
          padding: 0;
          margin: 0;
          background: none;
          cursor: pointer;
        }
      }
    }
  }
  .title-bar {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    position: relative;
    transition: padding 300ms ease-in-out;
    @include breakpoint(md) {
      padding: 30px 0;
    }
    @include breakpoint(lg) {
      padding: 0;
    }
  }
  .mobile-nav-btn {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
  }
  .logo-wrapper {
    width: 40px;
    transition: width 300ms ease-in-out;
    a {
      display: block;
      line-height: 0;
    }
    @include breakpoint(lg) {
      width: 200px;
      padding-right: 40px;
      align-items: flex-end;
      &__scrolled {
        width: 40px;
        margin-right: 40px;
      }
    }
  }
  .logo {
    display: block;
    width: 100%;
    margin: 0 auto;
    &__scrolled {
      @include breakpoint(lg) {
        max-width: 40px;
        width: 40px;
        filter: invert(1);
      }
      &-dark {
        filter: invert(0);
      }
    }
    &--mobile {
      display: block;
      width: 100%;
      margin: 0 auto;
      &__scrolled {
        filter: invert(1);
        margin: 0 auto;
        display: block;
        max-width: 220px;
      }
      &__dark {
        filter: invert(0);
      }
      &-top-nav-open {
        filter: invert(1);
      }
    }
  }
  .nav-menu {
    display: none;
    ul {
      margin: 0;
      padding: 0;
      color: $on-surface;
      max-width: $lg;
      li {
        display: block;
        float: left;

        &:not(:last-child) {
          margin-right: 0;
        }
      }
    }
    ul::after {
      content: "";
      display: block;
      clear: both;
    }
  }
  .nav-icons {
    &-wrapper {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      > *:not(:last-child) {
        margin-right: 14px;
      }
      @include breakpoint(lg) {
        flex: 1;
        > *:not(:last-child) {
          margin-right: 24px;
        }
      }
    }
    &--account {
      display: none;
      @include breakpoint(md) {
        display: block;
      }
    }
    &--search-btn {
      background: none;
      border: none;
      margin: 0;
      padding: 0;
      @include breakpoint(lg) {
      }
    }
    &--region-selector {
      display: none;
      @include breakpoint(lg) {
        display: block;
      }
      &__btn {
        border: none;
        padding: 0;
        margin: 0;
        background: none;
        cursor: pointer;
      }
    }
  }
  .mobile-nav-wrapper {
    position: static;
  }
  @include breakpoint(lg) {
    .nav-menu {
      display: block;
    }
    .mobile-nav-btn {
      display: none;
    }
  }
  .search {
    border-top: 1px solid $background;
    background-color: $surface;
    position: absolute;
    width: 100%;
    top: 100%;
  }
</style>

<svelte:window
  bind:innerWidth={viewportWidth}
  on:keydown={e => {
    if (e.key === "Escape") {
      $search = false;
    }
  }}
/>
<div
  class="nav-header"
  style="top: -{bannerShowing
    ? isMobile
      ? bannerHasLinks
        ? 71
        : 56
      : 40
    : 0}px;"
  class:isVisible
>
  {#if !logoOnly && banners && banners.length > 0}
    <BannerWrapper
      {banners}
      {bannerHasLinks}
      on:closeBanner={() => (bannerShowing = false)}
    />
  {/if}
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="nav--wrapper {!isMobile &&
    scrollTop > scrollTopThreshold &&
    !scrolledUp
      ? 'nav--wrapper__no-inter'
      : ''}"
    on:mouseleave={onMouseExitNav}
  >
    {#if isMobile || scrollTop <= scrollTopThreshold || scrolledUp}
      <div
        transition:fly={{
          y: -200,
          duration: 300,
          easing: circInOut,
          opacity: 1,
        }}
      >
        <nav
          class={`nav ${
            !isLandingPage
              ? dark
                ? "nav--has-border nav--light-bg"
                : "nav--has-border"
              : ""
          } ${mobileNavOpen ? "sm-nav-open" : ""} ${
            dark ? "nav--dark" : "nav--light"
          } ${darkBg ? "nav--dark-bg" : ""} ${
            dark && scrolledUp && scrollTop > scrollTopThreshold
              ? "nav--dark__scrolled"
              : !dark && scrolledUp && scrollTop > scrollTopThreshold
                ? "nav--light__scrolled"
                : ""
          } ${
            ((isMobile && scrollTop > scrollTopThreshold) || mobileNavOpen) &&
            dark
              ? "nav--mobile__dark"
              : (isMobile && scrollTop > scrollTopThreshold) || mobileNavOpen
                ? "nav--mobile__scrolled"
                : ""
          }
           ${bannerShowing ? "nav--border-top" : ""}`}
        >
          <Container>
            <div class="title-bar">
              {#if !logoOnly}
                {#if isMobile || scrollTop <= scrollTopThreshold || scrolledUp}
                  <div
                    class={!isMobile &&
                    scrolledUp &&
                    scrollTop > scrollTopThreshold
                      ? "logo-wrapper__scrolled"
                      : "logo-wrapper"}
                  >
                    <a href={linkTo("/")}>
                      {#if !isMobile}
                        {#if scrolledUp && scrollTop > scrollTopThreshold}
                          <img
                            src={`${assets}/brandmark-dark.svg`}
                            alt="Belstaff Logo"
                            width="40"
                            height="41"
                            class="logo__scrolled {dark
                              ? 'logo__scrolled-dark'
                              : ''}"
                          />
                        {:else}
                          <img
                            class="logo"
                            src={dark
                              ? `${assets}/logo-dark.svg`
                              : `${assets}/logo-light.svg`}
                            alt="Belstaff Logo"
                            width="160"
                            height="32"
                          />
                        {/if}
                      {/if}
                      {#if isMobile}
                        {#if scrollTop > scrollTopThreshold || mobileNavOpen}
                          <img
                            class="logo--mobile {dark
                              ? 'logo--mobile__dark'
                              : 'logo--mobile__scrolled'}"
                            src={`${assets}/brandmark-dark.svg`}
                            alt="Belstaff Logo"
                            width="40"
                            height="41"
                          />
                        {:else if dark}
                          <img
                            class="logo--mobile logo--mobile-top-nav-open logo--mobile__scrolled"
                            src={`${assets}/brandmark-light.svg`}
                            alt="Belstaff Logo"
                            width="40"
                            height="41"
                          />
                        {:else if !dark}
                          <img
                            class="logo--mobile logo--mobile-top-nav-open logo--mobile__scrolled"
                            src={`${assets}/brandmark-dark.svg`}
                            alt="Belstaff Logo"
                            width="40"
                            height="41"
                          />
                        {/if}
                      {/if}
                    </a>
                  </div>
                {/if}
                <nav class="nav-menu" aria-label="Main navigation">
                  <ul role="menubar">
                    {#if nav}
                      {#each navItems as navItem}
                        <li role="none">
                          <NavMenuItem
                            {dark}
                            shrink={!isMobile &&
                              scrolledUp &&
                              scrollTop > scrollTopThreshold}
                            {navItem}
                            on:onMouseExitNav={onMouseExitNav}
                          />
                        </li>
                      {/each}
                    {/if}
                  </ul>
                </nav>
              {/if}
              {#if !logoOnly}
                <div class="nav-icons-wrapper" on:mouseenter={onMouseExitNav}>
                  <div class="nav-icons--region-selector">
                    <RegionSelectorCTA
                      {language}
                      {country}
                      theme={colourTheme}
                    />
                  </div>
                  {#if searchEnabled || $locale.includes("en")}
                    <button
                      type="button"
                      class="nav-icons--search-btn"
                      on:click={() => {
                        if (isMobile) {
                          openMobileSearch();
                        } else {
                          $search = !$search;
                        }
                      }}
                      on:keydown={() => {
                        if (isMobile) {
                          openMobileSearch();
                        } else {
                          $search = !$search;
                        }
                      }}
                      aria-label={$_("Search")}
                    >
                      <Icon icon="search" color={iconTheme} />
                    </button>
                  {/if}
                  <div class="nav-icons--account">
                    <Link
                      href="account"
                      icon="person_outline"
                      text=""
                      underline={false}
                      theme={colourTheme}
                      iconTheme={colourTheme}
                      hoverTheme={mobileNavOpen && dark
                        ? "on-surface"
                        : scrollTop > scrollTopThreshold && dark
                          ? "on-surface"
                          : scrollTop < scrollTopThreshold && dark
                            ? "on-surface"
                            : dark
                              ? "on-surface"
                              : "surface"}
                      noMargin={true}
                      aria-label={$_("My account")}
                    />
                  </div>
                  <a
                    href={linkTo("bag")}
                    class="nav-icons--basket"
                    tabindex="0"
                    on:click={e => {
                      isMobile && e.preventDefault();
                      closeMobileNav();
                      isMobile && miniCart.handleOpenMiniCart();
                      if (isMobile) {
                        disableAutoDismiss = true;
                      }
                    }}
                    on:keydown={() => {
                      closeMobileNav();
                      isMobile && miniCart.handleOpenMiniCart();
                    }}
                    on:mouseenter={handleMiniCartHover}
                    on:mouseleave={handleMiniCartHoverLeave}
                    aria-label={$_("Bag")}
                  >
                    <BasketStatus {quantity} color={iconTheme} {dark} />
                  </a>
                  <button
                    type="button"
                    class="mobile-nav-btn"
                    on:click={toggleMobileNav}
                    title="Toggle Menu"
                  >
                    {#if mobileNavOpen}
                      <Icon icon="close" color={iconTheme} />
                    {:else}
                      <Icon icon="menu" color={iconTheme} />
                    {/if}
                  </button>
                </div>
              {/if}
            </div>
          </Container>
          {#if mobileNavOpen}
            <MobileNav
              on:toggleMobileNav={toggleMobileNav}
              on:keydown={toggleMobileNav}
              {nav}
              {language}
              {country}
              {dark}
            />
          {/if}
          {#if $search === true}
            <div class="search" transition:fade={{ duration: 200 }}>
              <SearchInput />
            </div>
          {/if}
        </nav>
      </div>
    {/if}
  </div>
  <MiniCart bind:this={miniCart} {regionInfo} {disableAutoDismiss} />
</div>
{#if mobileSearchOpen}
  <div class="mobile-search">
    <MobileSearch on:closeMobileSearch={closeMobileSearch} />
  </div>
{/if}
